'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { AppWindow, Compass, Expand, Grid2X2, LogIn, Maximize } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/avatar';
import { toggleAppPinch, useAppPinch } from '@/components/workspace/app-pinch';
import { useNotifications } from '@/hooks/useNotifications';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import { useOnscreenKeyboard } from '@/hooks/use-onscreen-keyboard';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import {
  openWorkspacePanel,
  useWorkspacePanelOpen,
  useWorkspacePanelTab,
} from '@/components/workspace/panels/panel-events';
import {
  closeDiscover,
  requestDiscoverToggle,
  useDiscoverOpen,
} from '@/components/workspace/discover/discover-events';
import {
  useFullscreenItemId,
  useHandsetFullscreen,
  useOverviewMode,
  usePresentedWindowId,
  useWorkspaceState,
} from '@/components/workspace/store';
import type { UserMenuUser } from '@/components/user-menu-dropdown';

/**
 * The bar's MEASURED height in px, published on the root as `--ws-bar-px`
 * (0px while hidden) and announced with `ws:bar-measured` so the canvas can
 * re-measure its usable box without a window resize.
 *
 * WHY MEASURED (RV-034, 2026-09-29). `--ws-bar-b` says `3.5rem` while the bar
 * is `3.5rem + env(safe-area-inset-bottom)` tall, about 80 px on the tester's
 * Pixel 8 Pro, and `env()` never resolves to px through a custom property. So
 * every consumer that subtracted the variable came up 24 px short and drew
 * windows under the navigation. The bar knows its own box; it says so.
 */
export const BAR_PX_VAR = '--ws-bar-px';
export const BAR_MEASURED_EVENT = 'ws:bar-measured';

/** The height to publish for a bar element, or 0 for none. Rounded: a fractional bar is a fractional app. */
export function measuredBarPx(el: { getBoundingClientRect(): { height: number } } | null): number {
  if (!el) return 0;
  const h = el.getBoundingClientRect().height;
  return Number.isFinite(h) && h > 0 ? Math.round(h) : 0;
}

function publishBarPx(px: number): void {
  const root = document.documentElement;
  const next = `${px}px`;
  if (root.style.getPropertyValue(BAR_PX_VAR) === next) return;
  root.style.setProperty(BAR_PX_VAR, next);
  window.dispatchEvent(new CustomEvent(BAR_MEASURED_EVENT, { detail: { px } }));
}

/**
 * The mobile bottom bar (spec §9, option 2 + U2): Discover · Stage ·
 * You, below md. Phones have no rail and no chords, so
 * before this the only route to Discover was two taps deep in the drawer and
 * Stage had NO touch entry at all.
 *
 * The account slot is a TAP, deliberately not a gesture: mobile browsers own
 * the edge swipes (left is back, right is forward — the same finding that
 * made the drawer tap-based), and horizontal swipes on the canvas belong to
 * SCREEN SWITCHING. Identity is a destination, not a direction. The bar
 * mirrors the desktop rail's shape: navigation first, the avatar at the end.
 *
 * Hidden under a fullscreen takeover, like the rest of the shell chrome.
 */
export function MobileTabBar({ user }: { user: UserMenuUser | null }) {
  const t = useT();
  const isMd = useIsMd();
  // A rotated phone renders the DESKTOP rail instead (the full reel:
  // nav + taskbar + avatar) - the bar only exists in portrait.
  const landscape = useHandsetLandscape();
  // Same SSR trap the rail solves with `railMounted`: `useIsMd` starts FALSE
  // (SSR-safe) and flips after mount, so without this gate the bar rendered
  // on DESKTOP for the first client frames of every reload, then vanished.
  // Treat "not yet mounted" as desktop: SSR never carries the bar, phones
  // get it one frame after mount.
  const [mounted, setMounted] = useState(false);
  // Layout effect, not effect: it must resolve BEFORE the hydrated frame
  // paints, or phones flash chrome-less (and used to flash the desktop
  // rail) for a beat on every load.
  useIsomorphicLayoutEffect(() => setMounted(true), []);
  const fullscreenItemId = useFullscreenItemId();
  const overviewMode = useOverviewMode();
  const discoverOpen = useDiscoverOpen();
  // Camera zoom, for the conditional zoom chip: visible only while the
  // camera is away from 100%, so the bar stays three slots in the normal
  // resting state and the chip reads as "something to fix".
  const zoomPct = Math.round(
    (useWorkspaceState((s) => s.workspace?.cameraZoom ?? 1) || 1) * 100,
  );
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const appPinch = useAppPinch();
  const authed = !!user;
  const { unreadCount } = useNotifications(authed);
  const { openHref } = useFullscreenNav();
  const panelOpen = useWorkspacePanelOpen('left');
  const panelTab = useWorkspacePanelTab('left');

  // WIDTH-gated like the drawer and the sheet (the shell's convention for
  // navigation chrome), not pointer-gated: a narrow desktop window gets the
  // bar too, which is also how this gets dogfooded. Touch-only affordances
  // (the swipe pager, the Hand FAB) stay coarse-gated separately.
  // While the on-screen keyboard is up the bar HIDES (standard mobile
  // pattern): the keyboard needs the pixels, and on Android the shrunken
  // layout viewport would otherwise leave the bar riding directly above it.
  const keyboardUp = useOnscreenKeyboard();
  // The phone app view (handoff 2026-09-29): while a window is presented, the
  // in-tab full screen hides ONLY this bar (the app bar stays, with its ad),
  // and the Zoom slot hides because there is nothing to zoom (decision 2).
  // Both gates need a PRESENTED window: on a blank screen the bar is whole.
  const presented = usePresentedWindowId();
  const handsetFullscreen = useHandsetFullscreen();
  const appPresented = !!presented;
  const hidden =
    !mounted || isMd || landscape || !!fullscreenItemId || keyboardUp || (handsetFullscreen && appPresented);

  // Publish the measured height (see BAR_PX_VAR): on mount, on every resize
  // of the bar's own box (a rotation, a font change, the inset arriving late),
  // and 0 the moment it hides. Layout effect so the canvas never measures a
  // frame with the bar drawn and the variable still at 0.
  const navRef = useRef<HTMLElement>(null);
  useIsomorphicLayoutEffect(() => {
    if (hidden) {
      publishBarPx(0);
      return;
    }
    const el = navRef.current;
    publishBarPx(measuredBarPx(el));
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => publishBarPx(measuredBarPx(el)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hidden]);
  useEffect(
    () => () => {
      publishBarPx(0);
      document.documentElement.style.removeProperty(BAR_PX_VAR);
    },
    [],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (hidden) {
      root.style.setProperty('--ws-bar-b', '0px');
      root.style.setProperty('--ws-bar-l', '0px');
    } else {
      // Plain height: every consumer adds env(safe-area-inset-bottom)
      // itself, so landscape (var 0) still clears a notch chin.
      root.style.setProperty('--ws-bar-b', '3.5rem');
      root.style.setProperty('--ws-bar-l', '0px');
    }
    return () => {
      root.style.removeProperty('--ws-bar-b');
      root.style.removeProperty('--ws-bar-l');
    };
  }, [hidden, landscape]);

  if (hidden) return null;

  const goScreens = () => {
    // Stage under the overlay would be invisible; the two are exclusive.
    if (discoverOpen) closeDiscover();
    window.dispatchEvent(
      new CustomEvent('ws:screen', { detail: { kind: 'overview-toggle' } }),
    );
  };

  const slot =
    'pointer-events-auto relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground transition-colors';
  const active = 'text-foreground';
  // Desktop rail parity (rail-item's compact variant): the pressed state is
  // a filled pad behind the glyph, not a colour swap alone — the bar speaks
  // the same language as Discover/Stage on the rail.
  const pad =
    'inline-flex h-8 min-w-9 items-center justify-center rounded-xl px-2 transition-colors';
  const padOn = 'bg-foreground/[0.08]';

  return (
    <nav
      ref={navRef}
      aria-label={t('workspace.canvas.workspace')}
      data-testid="workspace-mobile-tab-bar"
      data-app-presented={appPresented ? 'true' : undefined}
      className={cn(
        'pointer-events-auto absolute inset-x-0 bottom-0 z-[96] grid border-t border-border/70 bg-background',
        // Three slots while an app is presented (Zoom hides, decision 2 of
        // the handoff); the permanent fourth slot returns on a blank screen.
        appPresented ? 'grid-cols-3' : 'grid-cols-4',
      )}
      style={{
        height: 'calc(3.5rem + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <button
        type="button"
        onClick={() => requestDiscoverToggle()}
        className={cn(slot, discoverOpen && active)}
        aria-label={t('nav.discover')}
        aria-pressed={discoverOpen}
        data-testid="mobile-tab-discover"
      >
        <span className={cn(pad, discoverOpen && padOn)}>
          <Compass className="h-5 w-5" strokeWidth={discoverOpen ? 2.2 : 1.8} />
        </span>
        {t('nav.discover')}
      </button>
      {/* Stage is ALWAYS present (user call): an empty workspace still has
          the Stage sheet (screens + "+ New screen"), and a slot that
          appears when the first app lands made the bar unstable. */}
      {(
        <button
          type="button"
          onClick={goScreens}
          className={cn(slot, overviewMode && active)}
          aria-label={t('workspace.rail.stage')}
          aria-pressed={overviewMode}
          data-testid="mobile-tab-screens"
        >
          <span className={cn(pad, overviewMode && padOn)}>
            <Grid2X2 className="h-5 w-5" strokeWidth={overviewMode ? 2.2 : 1.8} />
          </span>
          {t('workspace.rail.stage')}
        </button>
      )}
      {/* Zoom is a PERMANENT slot on a blank screen (user call: an appearing
          slot made the bar layout unstable). The glyph is the live
          percentage; a tap opens a three-FAB flyout above the slot: 1x, fit
          the current screen, fit everything. While an app is PRESENTED the
          slot hides (the owner's decision 2, 2026-09-29): the app fills the
          tab at 100% and there is nothing to zoom. */}
      {!appPresented && (
      <div className="relative flex">
        {zoomMenuOpen && (
          <>
            <button
              type="button"
              aria-label={t('mobileBar.closeZoomMenu')}
              onClick={() => setZoomMenuOpen(false)}
              className="fixed inset-0 z-[97] cursor-default"
            />
            <div
              className="absolute left-1/2 z-[98] flex -translate-x-1/2 flex-row items-center gap-2.5"
              // Inline: the arbitrary-calc Tailwind class failed to lift the
              // row above the bar, leaving the FABs sitting on the
              // percentage label.
              style={{ bottom: 'calc(100% + 0.875rem)' }}
            >
              {[
                {
                  kind: 'zoom-100',
                  label: 'Zoom to 100%',
                  glyph: (
                    <span className="text-[13px] font-semibold leading-none">
                      1×
                    </span>
                  ),
                },
                {
                  kind: 'zoom-fit-screen',
                  label: 'Fit this screen',
                  glyph: <Maximize className="h-4.5 w-4.5" strokeWidth={1.8} />,
                },
                {
                  kind: 'zoom-fit-all',
                  label: 'Fit all screens',
                  glyph: <Expand className="h-4.5 w-4.5" strokeWidth={1.8} />,
                },
              ].map((a) => (
                <button
                  key={a.kind}
                  type="button"
                  aria-label={a.label}
                  onClick={() => {
                    setZoomMenuOpen(false);
                    window.dispatchEvent(
                      new CustomEvent('ws:screen', {
                        detail: { kind: a.kind },
                      }),
                    );
                  }}
                  className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-background text-foreground shadow-lg"
                >
                  {a.glyph}
                </button>
              ))}
              {/* Pinch destination TOGGLE, not an action: while on, a
                  two-finger gesture over an app zooms the APP (its own
                  canvas), not the workspace camera. Stays open on tap so
                  the state change is visible where it happened. */}
              <button
                type="button"
                aria-label={t('mobileBar.pinchZooms')}
                aria-pressed={appPinch}
                onClick={() => toggleAppPinch()}
                className={cn(
                  'pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-lg',
                  appPinch
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border/70 bg-background text-foreground',
                )}
              >
                <AppWindow className="h-4.5 w-4.5" strokeWidth={1.8} />
              </button>
            </div>
          </>
        )}
        <button
          type="button"
          onClick={() => setZoomMenuOpen((v) => !v)}
          className={cn(slot, 'w-full', zoomMenuOpen && active)}
          aria-label={`Zoom, currently ${zoomPct}%`}
          aria-pressed={zoomMenuOpen}
          aria-expanded={zoomMenuOpen}
          data-testid="mobile-tab-zoom"
        >
          <span
            className={cn(
              pad,
              'text-[12px] font-semibold leading-none tracking-tight',
              zoomMenuOpen && padOn,
            )}
          >
            {zoomPct}%
          </span>
          Zoom
        </button>
      </div>
      )}
      {authed && user ? (
        // U2 on mobile too: the bell slot is gone — the unread badge rides
        // the You avatar, and Notifications is the first row of the account
        // sheet this opens (one tap + one row, same as desktop).
        <button
          type="button"
          onClick={() => openWorkspacePanel('left', { tab: 'account' })}
          className={cn(slot, panelOpen && panelTab === 'account' && active)}
          aria-label={
            unreadCount > 0 ? `You · ${unreadCount} unread notifications` : 'You'
          }
          data-testid="mobile-tab-account"
        >
          <span
            className={cn(pad, panelOpen && panelTab === 'account' && padOn)}
          >
            <span className="relative">
              <Avatar
                seed={user.username}
                type={user.accountType === 'agent' ? 'agent' : 'human'}
                src={user.avatarUrl}
                className="h-5 w-5"
              />
              {unreadCount > 0 && (
                <span className="absolute -right-2 -top-1 grid h-3.5 min-w-3.5 place-items-center rounded-full bg-destructive px-1 text-[9px] font-semibold leading-none text-destructive-foreground ring-2 ring-background">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </span>
          </span>
          You
        </button>
      ) : (
        <button
          type="button"
          onClick={() => openHref('/signup')}
          className={slot}
          data-testid="mobile-tab-signup"
        >
          <span className={pad}>
            <LogIn className="h-5 w-5" strokeWidth={1.8} />
          </span>
          {t('home.cta.signUp')}
        </button>
      )}
    </nav>
  );
}
